import React, { useEffect } from 'react';
import { Trophy, Target } from 'lucide-react';
import { Card, CardHeader, CardFooter, AddButton, ProgressBar, CollapsibleContent, EditModeToggle, EditModeCheckbox, EditModeActionBar } from '../shared';
import { InlineEditField } from '../shared/InlineEditField';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useFinancialData } from '../../contexts/FinancialDataContext';
import { useInlineEdit } from '../../hooks/useInlineEdit';
import { useCollapsedWidgets } from '../../hooks/useCollapsedWidgets';
import { useFinancialCalculations } from '../../hooks/useFinancialCalculations';
import { useEditMode } from '../../hooks/useEditMode';
import { formatCurrency } from '../../utils/formatters';

function formatGoalValue(value: number, unit: string, privacyMode: boolean): string {
  if (privacyMode) return '••••••';
  if (unit === 'USD') return formatCurrency(value);
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 4 })} ${unit}`;
}

interface GoalsWidgetProps {
  onAdd: () => void;
  variant?: 'card' | 'page';
}

export const GoalsWidget: React.FC<GoalsWidgetProps> = ({ onAdd, variant = 'card' }) => {
  const { darkMode, privacyMode } = useTheme();
  const { sheetId } = useAuth();
  const { updateGoal, handleDeleteGoal, totalGoalsSaved, totalGoalsTarget, handleBatchDelete } = useFinancialData();
  const { goalProgress } = useFinancialCalculations();
  const { editValue, setEditValue, isEditing, startEdit, cancelEdit, completeEdit } = useInlineEdit();
  const { isCollapsed, toggleCollapse } = useCollapsedWidgets();
  const collapsed = isCollapsed('goals');

  const handleDeleteAsync = async (index: number) => {
    if (!sheetId) return;
    await handleDeleteGoal(index, sheetId);
  };

  const handleBatchDeleteAsync = async (indices: number[]) => {
    if (!sheetId) return;
    await handleBatchDelete('Goals', indices, sheetId);
  };

  const editMode = useEditMode(handleDeleteAsync, goalProgress.length, handleBatchDeleteAsync);
  const { exitEditMode } = editMode;

  // Exit edit mode when widget collapses
  useEffect(() => {
    if (collapsed) exitEditMode();
  }, [collapsed, exitEditMode]);

  const handleSave = async (index: number) => {
    const { value, isValid } = completeEdit();
    if (isValid && sheetId) {
      await updateGoal(index, 'currentAmount', value, sheetId);
    }
  };

  const totalPercentage = totalGoalsTarget > 0 ? (totalGoalsSaved / totalGoalsTarget) * 100 : 0;

  const isPage = variant === 'page';

  return (
    <Card variant={variant}>
      <CardHeader
        title="Financial Goals"
        icon={<Target size={20} />}
        action={
          <div className="flex items-center gap-2">
            <EditModeToggle
              isEditMode={editMode.isEditMode}
              onToggle={editMode.toggleEditMode}
              itemCount={goalProgress.length}
            />
            <AddButton onClick={onAdd} />
          </div>
        }
        collapsed={isPage ? false : collapsed}
        onToggleCollapse={isPage ? undefined : () => toggleCollapse('goals')}
        variant={variant}
      />

      <CollapsibleContent collapsed={isPage ? false : collapsed} variant={variant}>
      {editMode.isEditMode && (
        <EditModeActionBar
          selectedCount={editMode.selectedIndices.size}
          totalCount={goalProgress.length}
          allSelected={editMode.selectedIndices.size === goalProgress.length}
          onSelectAll={() => editMode.selectAll(goalProgress.length)}
          onDeselectAll={editMode.deselectAll}
          onDeleteSelected={editMode.deleteSelected}
          isDeleting={editMode.isDeleting}
        />
      )}

      <div className="space-y-4 overflow-x-auto">
        {goalProgress.map((goal, idx) => {
          return (
            <div key={idx}>
              <div
                className={editMode.isEditMode ? 'flex items-start gap-2' : ''}
              >
                {editMode.isEditMode && (
                  <div className="pt-1 flex-shrink-0">
                    <EditModeCheckbox
                      checked={editMode.selectedIndices.has(idx)}
                      onToggle={() => editMode.toggleIndex(idx)}
                    />
                  </div>
                )}
                <div className={editMode.isEditMode ? 'flex-1 min-w-0' : 'contents'}>
                <div className="flex justify-between items-center mb-1">
                  <div className="flex-1 min-w-0">
                    <span className={`text-sm font-medium ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      {goal.name}
                    </span>
                    {goal.targetDate && (
                      <span className={`text-xs ml-2 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        by {goal.targetDate}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {goal.percentage >= 100 && (
                      <Trophy size={14} className="text-yellow-400" />
                    )}
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                      goal.percentage >= 100
                        ? 'bg-green-500/20 text-green-400'
                        : goal.onTrack
                        ? darkMode ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'
                        : darkMode ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {goal.percentage >= 100 ? 'Complete!' : `${goal.percentage.toFixed(0)}%`}
                    </span>
                  </div>
                </div>

                <ProgressBar
                  value={goal.currentAmount}
                  max={goal.targetAmount}
                  height="sm"
                  colorByUtilization={false}
                  customColor={goal.color}
                  showMilestones
                />

                {/* Milestone label */}
                {goal.percentage >= 25 && goal.percentage < 100 && (
                  <p className={`text-[10px] mt-0.5 ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}>
                    {goal.percentage >= 75 ? '75%' : goal.percentage >= 50 ? '50%' : '25%'} milestone reached!
                  </p>
                )}

                <div className="flex justify-between items-center mt-1 gap-3 text-xs">
                  <InlineEditField
                    value={goal.currentAmount}
                    isEditing={isEditing('goal', idx, 'currentAmount')}
                    editValue={editValue}
                    onEditValueChange={setEditValue}
                    onStartEdit={() => startEdit('goal', idx, 'currentAmount', goal.currentAmount)}
                    onSave={() => handleSave(idx)}
                    onCancel={cancelEdit}
                    editable={true}
                    variant="positive"
                    width={goal.unit !== 'USD' ? 'w-auto' : 'w-24 md:w-28'}
                    privacyMode={privacyMode}
                    formatFn={goal.unit !== 'USD' ? ((v, p) => p ? '••••••' : `${Number(v).toLocaleString(undefined, { maximumFractionDigits: 4 })} ${goal.unit}`) : undefined}
                  />
                  <span className={`text-xs whitespace-nowrap ${
                    goal.percentage >= 100
                      ? (darkMode ? 'text-green-400' : 'text-green-600')
                      : (darkMode ? 'text-slate-500' : 'text-slate-400')
                  }`}>
                    {goal.percentage >= 100 ? 'Goal complete!' : `${formatGoalValue(goal.remaining, goal.unit, privacyMode)} remaining`}
                  </span>
                </div>
                </div>
              </div>

            </div>
          );
        })}

        {goalProgress.length === 0 && (
          <div className={`text-center py-6 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            <p className="text-sm">No goals yet</p>
            <p className="text-xs mt-1">Set financial goals to track progress</p>
          </div>
        )}
      </div>

      </CollapsibleContent>

      <CardFooter>
        <div className="flex justify-between items-center font-semibold text-sm overflow-x-auto">
          <span className={`${darkMode ? 'text-white' : 'text-slate-800'} flex-shrink-0`}>Total Progress</span>
          <div className={`${darkMode ? 'text-violet-400' : 'text-violet-600'} text-right whitespace-nowrap`}>
            {totalGoalsTarget > 0
              ? `${totalPercentage.toFixed(0)}% (${formatCurrency(totalGoalsSaved, privacyMode)} / ${formatCurrency(totalGoalsTarget, privacyMode)})`
              : `${goalProgress.length} goal${goalProgress.length !== 1 ? 's' : ''}`
            }
          </div>
        </div>
      </CardFooter>
    </Card>
  );
};
